/// <amd-module name="SearchUI/Components/SearchPickList/SearchPickList.Props" />
import * as React from "react";
import { IFilterBarItemProps } from "SearchUI/FilterBar";
import { IFocusableProps } from "VSSUI/Utilities/Focus";

export interface ISearchPickListItem {
    /**
    * Checkbox state of the list item.
    */
    selected: boolean;

    /**
     * Facet count that needs to be shown against the name of the item.
     */
    resultCount: number;

    /**
     * A unique string identifying the item.
     */
    id: string;

    /**
     * The value which is displayed to the user as name of the item.
     */
    name: string;
}

export interface ISearchPickListProps {
    /**
     * A unique string which represents the Pick list control.
     */
    name: string;

    /**
    * Width of the area which displays the text.
    */
    width: number;

    /**
     * True when the control is enabled, false otherwise.
     */
    enabled: boolean;

    /**
     * The list of item shown in the drop-down menu.
     */
    items: ISearchPickListItem[];

    /**
     * The left hand side part of full string labelled as button text.
     */
    displayName: string;

    /**
     * The label associated to "All" item in the drop-down menu which is shown as part of button text if item is selected.
     */
    allItemLabel: string;

    /**
     * The label associated to "All" item when it is listed in the drop-down menu.
     */
    allItemDisplayName: string;

    /**
     * Setting this will not compute the label to be displayed in the combo box based on best fit approach which uses measurement
     */
    lightWeightDisplay?: boolean;

    /**
     * Setting to disable announcing dropdown status message.
     */
    disableAnnounce?: boolean;

    className?: string;

    /**
     * Set this to "true" when search within the list needs to be supported.
     */
    searchable?: boolean;

    /**
     * Maximum width the search pick list menu can be resized upto
     */
    menuMaxWidth?: number;

    /**
     * Property to decide wether to render expandable picklist or normal picklist
     */
    isExpandable?: boolean;

    /**
     * Callback to define the logic to refine the list of items given the search text.
     */
    onRefineItems?: (items: ISearchPickListItem[], searchText: string) => ISearchPickListItem[];

    /**
     * Delegate invoked whenever item selection happens.
     */
    onSelectionChanged?: (name: string, selectedItems: ISearchPickListItem[]) => void;

    /**
    * Deletegate invoked to get the string to be displayed in the footer section of the drop-down menu.
    */
    onGetFooterMessage?: (itemCount: number, searchText: string) => string;

    /**
     * Water mark text of the search box rendered when the control supports search in the list.
     */
    searchTextPlaceholder?: string;

    /**
     * Delegate invoked on user action e.g. hover on help icon to trigger callout.
     */
    triggerCallout?: (showCallout: boolean) => void;

    /**
     * Object to hold callout properties such as the title, and content.
     */
    calloutProps?: { [key: string]: string };

    /**
     * Set this to "true" for a display only mode when there is only one item to be displayed.
     */
    compactMode?: boolean;

    /**
     * Set this to "true" for not displaying the info Icon. Default value is "false"
     */
    hideInfoIcon?: boolean;

    /**
     * Object to hold callout properties such as the title, and content.
     */
    compactModeCalloutProps?: { [key: string]: string };

    /**
     * Method to invoke on Click on filter
     */
    onClick?: () => void;

    /**
     * Param to check if spinner is tobe displayed
     */
    dataLoading?: boolean;
}

export interface ISearchPickListItemProps {
    /**
     * Item that the component renders.
     */
    item: ISearchPickListItem;

    /**
     * Total number of items shown in the list
     */
    itemsCount: number;

    /**
     * Index of the item in the list
     */
    itemIndex: number;

    /**
     * If "true" item will not be selectable.
     */
    disabled: boolean;

    /**
     * Specify focus related behavior. e.g. if the items needs to be skipped while navigating through keyboard.
     */
    focusableProps?: IFocusableProps;
    
    /**
     * Delegate invoked on item selection.
     */
    onChange?: (item: ISearchPickListItem, isChecked: boolean) => void;

    /**
     * Optional delegated invoked whenever the item is get mounted as part of the control on the page.
     */
    onMount?: () => void;
}

export interface ISearchPickListMenuProps {
    /**
     * Items rendered as selectable controls in the drop-down menu.
     */
    items: ISearchPickListItem[];

    /**
     * The label associated to "All" item when it is listed in the drop-down menu.
     */
    allItemDisplayName: string;

    /**
     * A unique string used as "id" attribute of control's root element.
     */
    id?: string;

    /**
     * Set this to "true" when search within the list needs to be supported.
     */
    searchable?: boolean;

    /**
     * Setting to disable announcing dropdown status message.
     */
    disableAnnounce?: boolean;

    /**
     * Callback to define the logic in order to refine the list of items given the search text.
     */
    onRefineItems?: (items: ISearchPickListItem[], searchText: string) => ISearchPickListItem[];

    /**
     * Water mark text of the search box rendered when the control supports search in the list.
     */
    searchTextPlaceholder?: string;

    /**
     * Maximum width the search pick list menu can be resized upto
     */
    menuMaxWidth?: number;

    /**
     * Width of the search pick list menu
     */
    width?: number;

    /**
     * Height of the search pick list menu
     */
    height?: number;

    /*
     *  Delegate invoked whenever resizer is moved
     */
    onResizing?: (initialWidth: number, initialHeight: number, evt: React.MouseEvent<HTMLElement>) => void;

    /**
     * Property to decide wether to render expandable picklist or normal picklist
     */
    isExpandable?: boolean;

    /**
     * Delegate invoked whenever item selection happens.
     */
    onSelectionChanged?: (selectedItems: ISearchPickListItem[]) => void;

    /**
    * Deletegate invoked to get the string to be displayed in the footer section of the drop-down menu.
    */
    onGetFooterMessage?: (itemCount: number, searchText: string) => string;

    /**
     * Delegate invoked whenever the drop-down menu is dismissed.
     */
    onDismiss?: (evt?: React.SyntheticEvent<HTMLElement>) => void;

    /**
     * Param to check if spinner is tobe displayed
     */
    dataLoading?: boolean;
}

export interface ISearchPickListFilterBarItemProps extends IFilterBarItemProps, ISearchPickListProps {

}